import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { coachStartAPI } from '../services/api';

const CoachExperienceContext = createContext(null);
export const isCoachRequestCancelled = error => error?.code === 'ERR_CANCELED';
const cancelled = () => Object.assign(new Error('Request no longer belongs to this account'), { code: 'ERR_CANCELED' });
const validAdvice = advice => advice && typeof advice.focus === 'string' && typeof advice.practicalStep === 'string'
  && typeof advice.clarification === 'string' && Array.isArray(advice.assumptions)
  && advice.assumptions.every(item => typeof item === 'string');
const validState = value => value && ['starter', 'legacy'].includes(value.entry)
  && value.experienceVersion === 'coach-start.v1'
  && ['starter', 'handoff', 'insights'].every(flag => typeof value.flags?.[flag] === 'boolean')
  && (value.entry !== 'starter' || (value.flags.starter && Number.isInteger(value.draft?.revision) && value.draft.revision >= 0))
  && (!value.flags.starter || (Array.isArray(value.teams) && (!value.draft?.intake
    || ['sport', 'ageGroup', 'challenge'].every(field => typeof value.draft.intake[field] === 'string'))))
  && (value.response?.status !== 'ready' || validAdvice(value.response.advice));

export function CoachExperienceProvider({ user, children }) {
  const ownerId = String(user?.id || user?._id || '');
  const isCoach = ['coach', 'club_admin'].includes(user?.role);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(Boolean(ownerId && isCoach));
  const [error, setError] = useState('');
  const [accountChanged, setAccountChanged] = useState(false);
  const [unsupported, setUnsupported] = useState(false);
  const [scopeOwner, setScopeOwner] = useState(ownerId);
  const controllers = useRef(new Set());
  const generation = useRef(0);
  const refreshSequence = useRef(0);
  const blocked = useRef(false);
  const currentOwner = useRef(ownerId);
  currentOwner.current = ownerId;

  const clearRequests = useCallback(() => {
    generation.current += 1;
    controllers.current.forEach(controller => controller.abort());
    controllers.current.clear();
    setData(null);
  }, []);

  const invalidate = useCallback(message => {
    clearRequests();
    blocked.current = true;
    setAccountChanged(true);
    setLoading(false);
    setError(message);
  }, [clearRequests]);

  const run = useCallback(async work => {
    if (blocked.current || !ownerId || currentOwner.current !== ownerId) throw cancelled();
    const controller = new AbortController();
    const currentGeneration = generation.current;
    controllers.current.add(controller);
    try {
      const result = await work({ signal: controller.signal, headers: { 'X-Coach-Owner': ownerId } });
      if (controller.signal.aborted || generation.current !== currentGeneration || currentOwner.current !== ownerId) {
        throw cancelled();
      }
      return result;
    } catch (err) {
      if (controller.signal.aborted || generation.current !== currentGeneration || currentOwner.current !== ownerId) throw cancelled();
      if (err.response?.data?.code === 'COACH_ACCOUNT_CHANGED') {
        invalidate('Your signed-in account changed. Reload to continue.');
        throw cancelled();
      }
      throw err;
    } finally {
      controllers.current.delete(controller);
    }
  }, [ownerId, invalidate]);

  const refresh = useCallback(async () => {
    if (!ownerId || !isCoach || blocked.current || currentOwner.current !== ownerId) return;
    const currentGeneration = generation.current;
    const sequence = ++refreshSequence.current;
    const isCurrent = () => currentGeneration === generation.current && sequence === refreshSequence.current
      && currentOwner.current === ownerId && !blocked.current;
    setLoading(true);
    setError('');
    setUnsupported(false);
    try {
      const result = await run(options => coachStartAPI.state(options));
      if (!isCurrent()) return;
      if (result.data?.ownerId !== ownerId) {
        invalidate('Your signed-in account changed. Reload to continue.');
        return;
      }
      if (!validState(result.data)) throw new Error('Invalid coaching setup response');
      setData(result.data);
      return result.data;
    } catch (err) {
      if (isCurrent() && !isCoachRequestCancelled(err)) {
        if (err.response?.status === 404) {
          setData(null);
          setUnsupported(true);
          setError('The coaching preview is not available on this server yet. You can still use the normal app.');
        } else setError(err.response?.data?.error || 'We could not check your coaching setup. Try again, or continue with the normal app.');
      }
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [ownerId, isCoach, run, invalidate]);

  useEffect(() => {
    clearRequests();
    blocked.current = false;
    setScopeOwner(ownerId);
    setAccountChanged(false);
    setUnsupported(false);
    setError('');
    if (ownerId && isCoach) refresh();
    else setLoading(false);
    return () => {
      blocked.current = true;
      clearRequests();
    };
  }, [ownerId, isCoach, user?.hasClub, clearRequests, refresh]);

  useEffect(() => {
    const signInChanging = () => invalidate('Your sign-in is changing. Reload if this screen stays open.');
    const storageChanged = event => {
      if (event.key !== 'user') return;
      let nextOwner = '';
      try {
        const next = JSON.parse(event.newValue || 'null');
        nextOwner = String(next?.id || next?._id || '');
      } catch { nextOwner = ''; }
      if (nextOwner !== currentOwner.current) signInChanging();
    };
    window.addEventListener('eliteedge:auth-changing', signInChanging);
    window.addEventListener('storage', storageChanged);
    return () => {
      window.removeEventListener('eliteedge:auth-changing', signInChanging);
      window.removeEventListener('storage', storageChanged);
    };
  }, [invalidate]);

  const value = useMemo(() => ({
    data: isCoach && data?.ownerId === ownerId ? data : null,
    loading: scopeOwner !== ownerId ? Boolean(ownerId && isCoach) : loading,
    error: scopeOwner === ownerId ? error : '',
    accountChanged: scopeOwner === ownerId && accountChanged,
    unsupported: scopeOwner === ownerId && unsupported, ownerId, refresh, run,
  }), [data, isCoach, scopeOwner, loading, error, accountChanged, unsupported, ownerId, refresh, run]);
  return <CoachExperienceContext.Provider value={value}>{children}</CoachExperienceContext.Provider>;
}

export function useCoachExperience() {
  return useContext(CoachExperienceContext);
}
